import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/client';
import { StatusPill } from '@/components/StatusPill';
import { DecisionsBadge, isDecisionsModel } from '@/components/DecisionsBadge';
import { fmtUsd } from '@/lib/format';
import type { AdminMcpServer, AdminModel, Caller, CallerStatus } from '@/types';

type Tab = 'All' | CallerStatus;
const tabs: Tab[] = ['All', 'Active', 'Disabled', 'Pending'];

export function Callers() {
  const api = useApi();
  const [tab, setTab] = useState<Tab>('All');

  const callers = useQuery({
    queryKey: ['admin-callers', tab],
    queryFn: () => api.adminListCallers(tab === 'All' ? undefined : tab)
  });

  // Enabled catalog models drive the per-caller restriction editor.
  const models = useQuery({ queryKey: ['admin-models'], queryFn: api.adminListModels });
  const enabledModels = (models.data ?? []).filter(m => m.enabled);

  // Registered MCP servers drive the per-caller tool grants editor.
  const mcpServers = useQuery({ queryKey: ['admin-mcp-servers'], queryFn: api.adminListMcpServers });
  const enabledMcpServers = (mcpServers.data ?? []).filter(s => s.enabled);

  const rows = callers.data ?? [];

  // Ctrl+K jumps here as #caller-<id>: bring that row into view and flash it once it has loaded.
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash.startsWith('#caller-') || !callers.data) return;
    const row = document.getElementById(hash.slice(1));
    if (!row) return;
    row.scrollIntoView({ block: 'center' });
    row.classList.add('row-flash');
    const t = setTimeout(() => row.classList.remove('row-flash'), 2400);
    return () => clearTimeout(t);
  }, [hash, callers.data]);

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Callers</h1>
          <div className="sub">Everyone and everything registered to call the gateway</div>
        </div>
      </div>

      <div className="toolbar">
        <div className="filter-tabs">
          {tabs.map(t => (
            <button key={t} className={`filter-tab ${tab === t ? 'on' : ''}`} onClick={() => setTab(t)}>
              {t}
            </button>
          ))}
        </div>
      </div>

      {callers.error && <div className="error-banner">Could not load callers: {(callers.error as Error).message}</div>}

      <div className="card" style={{ marginBottom: 22 }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Status</th>
              <th>Budget (USD)</th>
              <th>Models</th>
              <th>Tools</th>
              <th>Limits</th>
              <th className="num">
                Spend this month
                <div style={{ fontWeight: 400, fontSize: '.7rem', color: 'var(--kt-grey-400)' }}>resets 1st of month (UTC)</div>
              </th>
              <th>Notes</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {callers.isLoading ? (
              <tr><td colSpan={10} className="placeholder">Loading callers...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={10} className="placeholder">No callers{tab !== 'All' ? ` with status ${tab}` : ''}.</td></tr>
            ) : rows.map(c => <CallerRow key={c.id} caller={c} enabledModels={enabledModels} mcpServers={enabledMcpServers} />)}
          </tbody>
        </table>
      </div>

      <RegisterSystemCaller />
    </>
  );
}

function CallerRow({ caller, enabledModels, mcpServers }: {
  caller: Caller;
  enabledModels: AdminModel[];
  mcpServers: AdminMcpServer[];
}) {
  const api = useApi();
  const qc = useQueryClient();
  const [budget, setBudget] = useState(caller.monthlyBudgetUsd === null ? '' : String(caller.monthlyBudgetUsd));
  const [alertPct, setAlertPct] = useState(caller.alertThresholdPercent === null ? '' : String(caller.alertThresholdPercent));
  const [notes, setNotes] = useState(caller.notes ?? '');
  const [editingModels, setEditingModels] = useState(false);
  const [editingTools, setEditingTools] = useState(false);
  const [compare, setCompare] = useState(caller.compareModeEnabled);
  const [toolLimit, setToolLimit] = useState(String(caller.maxToolCallsLimit));

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin-callers'] });

  const save = useMutation({
    mutationFn: () => {
      // Empty clears the cap (unlimited) via the explicit flag; a positive number sets the cap.
      // 0 is no longer a magic "unlimited" value — the API rejects it.
      const trimmed = budget.trim();
      const patch = trimmed === ''
        ? { clearBudget: true, notes }
        : { monthlyBudgetUsd: Number(trimmed), notes };
      // Alert threshold rides the same save: empty = alerts off. For System callers the alert
      // email goes to the configured admin addresses.
      const pct = alertPct.trim();
      return api.adminUpdateCaller(caller.id, {
        ...patch,
        ...(pct === '' ? { clearAlertThreshold: true } : { alertThresholdPercent: Number(pct) }),
        compareModeEnabled: compare,
        maxToolCallsLimit: Number(toolLimit)
      });
    },
    onSuccess: invalidate
  });

  const toggleStatus = useMutation({
    mutationFn: () =>
      api.adminUpdateCaller(caller.id, { status: caller.status === 'Disabled' ? 'Active' : 'Disabled' }),
    onSuccess: invalidate
  });

  const budgetInvalid = budget.trim() !== '' && (Number.isNaN(Number(budget)) || Number(budget) <= 0);
  const alertPctInvalid = alertPct.trim() !== '' &&
    (Number.isNaN(Number(alertPct)) || Number(alertPct) < 1 || Number(alertPct) > 100 || !Number.isInteger(Number(alertPct)));
  const toolLimitInvalid = toolLimit.trim() === '' ||
    Number.isNaN(Number(toolLimit)) || Number(toolLimit) < 1 || !Number.isInteger(Number(toolLimit));
  const busy = save.isPending || toggleStatus.isPending;
  const error = (save.error ?? toggleStatus.error) as Error | null;

  return (
    <>
    <tr id={`caller-${caller.id}`}>
      <td>
        <div className="row-title">{caller.displayName}</div>
        <div className="row-sub">{caller.type === 'System' ? caller.apimSubscriptionName ?? 'system' : caller.email ?? ''}</div>
      </td>
      <td>{caller.type}</td>
      <td><StatusPill status={caller.status} /></td>
      <td>
        <input
          className="cell-input"
          type="number"
          min={0}
          step={1}
          placeholder="Unlimited"
          value={budget}
          onChange={e => setBudget(e.target.value)}
          aria-label={`Monthly budget for ${caller.displayName}`}
        />
        <div style={{ fontSize: '.72rem', color: 'var(--kt-grey-400)', marginTop: 2 }}>Empty = unlimited</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 6 }}>
          <input
            className="cell-input"
            type="number"
            min={1}
            max={100}
            step={1}
            placeholder="Off"
            value={alertPct}
            onChange={e => setAlertPct(e.target.value)}
            aria-label={`Budget alert percentage for ${caller.displayName}`}
            style={{ width: 64, borderColor: alertPctInvalid ? 'var(--kt-red)' : undefined }}
          />
          <span style={{ fontSize: '.72rem', color: 'var(--kt-grey-400)' }}>
            % alert{caller.type === 'System' ? ' (emails admins)' : ''}
          </span>
        </div>
      </td>
      <td style={{ whiteSpace: 'nowrap' }}>
        <span style={{ fontSize: '.85rem', color: caller.allowedModels ? 'var(--kt-orange)' : 'var(--kt-grey-500)' }}>
          {caller.allowedModels ? `${caller.allowedModels.length} allowed` : 'All models'}
        </span>{' '}
        <button className="btn-ghost btn-sm" onClick={() => setEditingModels(v => !v)}>
          {editingModels ? 'Close' : 'Edit'}
        </button>
      </td>
      <td style={{ whiteSpace: 'nowrap' }}>
        <span style={{ fontSize: '.85rem', color: caller.allowedMcpServers ? 'var(--kt-orange)' : 'var(--kt-grey-500)' }}>
          {caller.allowedMcpServers ? `${caller.allowedMcpServers.length} server${caller.allowedMcpServers.length > 1 ? 's' : ''}` : 'No tools'}
        </span>{' '}
        <button className="btn-ghost btn-sm" onClick={() => setEditingTools(v => !v)}>
          {editingTools ? 'Close' : 'Edit'}
        </button>
      </td>
      <td style={{ minWidth: 150 }}>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '.8rem', marginBottom: 5 }}>
          <input type="checkbox" checked={compare} onChange={e => setCompare(e.target.checked)} />
          <span title="One prompt across several models at once. Each model is billed separately.">
            Compare models
          </span>
        </label>
        <label style={{ display: 'block', fontSize: '.75rem', color: 'var(--kt-grey-500)' }}>
          Max tool calls
          <input
            type="number"
            min={1}
            value={toolLimit}
            onChange={e => setToolLimit(e.target.value)}
            style={{ width: 72, marginLeft: 6, padding: '3px 6px' }}
          />
        </label>
        {toolLimitInvalid && (
          <div style={{ color: 'var(--kt-red)', fontSize: '.72rem', marginTop: 3 }}>Whole number, 1 or more</div>
        )}
      </td>
      <td className="num">{fmtUsd(caller.spendThisMonthUsd)}</td>
      <td style={{ minWidth: 160 }}>
        <input
          className="cell-input wide"
          type="text"
          placeholder="Notes"
          value={notes}
          onChange={e => setNotes(e.target.value)}
          aria-label={`Notes for ${caller.displayName}`}
        />
      </td>
      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        {error && <div style={{ color: 'var(--kt-red)', fontSize: '.78rem', marginBottom: 4 }}>{error.message}</div>}
        <button className="btn-secondary btn-sm" onClick={() => save.mutate()} disabled={busy || budgetInvalid || alertPctInvalid || toolLimitInvalid}>
          {save.isPending ? 'Saving...' : 'Save'}
        </button>{' '}
        {caller.status !== 'Pending' && (
          <button
            className={caller.status === 'Disabled' ? 'btn-primary btn-sm' : 'btn-danger btn-sm'}
            onClick={() => toggleStatus.mutate()}
            disabled={busy}>
            {caller.status === 'Disabled' ? 'Enable' : 'Disable'}
          </button>
        )}
      </td>
    </tr>
    {editingModels && (
      <tr>
        <td colSpan={10} style={{ background: 'var(--kt-teal-bg)' }}>
          <ModelRestrictionEditor
            caller={caller}
            enabledModels={enabledModels}
            onSaved={() => { setEditingModels(false); invalidate(); }}
          />
        </td>
      </tr>
    )}
    {editingTools && (
      <tr>
        <td colSpan={10} style={{ background: 'var(--kt-teal-bg)' }}>
          <McpGrantsEditor
            caller={caller}
            mcpServers={mcpServers}
            onSaved={() => { setEditingTools(false); invalidate(); }}
          />
        </td>
      </tr>
    )}
  </>
  );
}

/** Per-caller MCP server grants — tools are default-deny, so nothing is ticked until granted. */
function McpGrantsEditor({ caller, mcpServers, onSaved }: {
  caller: Caller;
  mcpServers: AdminMcpServer[];
  onSaved: () => void;
}) {
  const api = useApi();
  const [selected, setSelected] = useState<Set<string>>(new Set(caller.allowedMcpServers ?? []));

  const save = useMutation({
    mutationFn: () => api.adminUpdateCaller(caller.id, { allowedMcpServers: Array.from(selected) }),
    onSuccess: onSaved
  });

  if (mcpServers.length === 0) {
    return (
      <div style={{ padding: '12px 4px', color: 'var(--kt-grey-500)', fontSize: '.9rem' }}>
        No enabled MCP servers registered yet — add one on the MCP servers page first.
      </div>
    );
  }

  return (
    <div style={{ padding: '12px 4px' }}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>
        MCP tool servers {caller.displayName} may use (unticked = no access):
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: 10 }}>
        {mcpServers.map(s => (
          <label key={s.name} title={s.description ?? undefined} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '.88rem' }}>
            <input
              type="checkbox"
              checked={selected.has(s.name)}
              onChange={() => setSelected(prev => {
                const next = new Set(prev);
                if (next.has(s.name)) next.delete(s.name); else next.add(s.name);
                return next;
              })}
            />
            <strong>{s.name}</strong>
            {s.description && <span style={{ color: 'var(--kt-grey-500)' }}>— {s.description}</span>}
          </label>
        ))}
      </div>
      {save.error && <div className="error-banner">Could not save: {(save.error as Error).message}</div>}
      <button className="btn-primary btn-sm" onClick={() => save.mutate()} disabled={save.isPending}>
        {save.isPending ? 'Saving...' : 'Save tool access'}
      </button>
    </div>
  );
}

function ModelRestrictionEditor({
  caller,
  enabledModels,
  onSaved
}: {
  caller: Caller;
  enabledModels: AdminModel[];
  onSaved: () => void;
}) {
  const api = useApi();
  const [restricted, setRestricted] = useState(caller.allowedModels !== null);
  const [selected, setSelected] = useState<Set<string>>(new Set(caller.allowedModels ?? []));

  const save = useMutation({
    mutationFn: () =>
      api.adminUpdateCaller(caller.id, { allowedModels: restricted ? Array.from(selected) : [] }),
    onSuccess: onSaved
  });

  const toggle = (key: string) =>
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const suppliers = Array.from(new Set(enabledModels.map(m => m.supplier)));

  return (
    <div style={{ padding: '12px 4px' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, marginBottom: 10 }}>
        <input type="checkbox" checked={!restricted} onChange={e => setRestricted(!e.target.checked)} />
        All enabled models (no restriction)
      </label>
      {restricted && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18 }}>
          {suppliers.map(supplier => (
            <div key={supplier} style={{ minWidth: 180 }}>
              <div style={{ fontWeight: 600, fontSize: '.82rem', color: 'var(--kt-heading)', marginBottom: 6 }}>
                {supplier}
              </div>
              {enabledModels.filter(m => m.supplier === supplier).map(m => {
                const key = `${m.supplier}/${m.modelId}`;
                return (
                  <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.85rem', marginBottom: 4 }}>
                    <input type="checkbox" checked={selected.has(key)} onChange={() => toggle(key)} />
                    {m.displayName}
                    {isDecisionsModel(m) && <DecisionsBadge />}
                  </label>
                );
              })}
            </div>
          ))}
        </div>
      )}
      {save.error && <div className="error-banner" style={{ marginTop: 8 }}>Could not save: {(save.error as Error).message}</div>}
      <div style={{ marginTop: 12 }}>
        <button
          className="btn-primary btn-sm"
          onClick={() => save.mutate()}
          disabled={save.isPending || (restricted && selected.size === 0)}>
          {save.isPending ? 'Saving...' : 'Save model access'}
        </button>
        {restricted && selected.size === 0 && (
          <span style={{ marginLeft: 10, fontSize: '.8rem', color: 'var(--kt-grey-400)' }}>
            Select at least one model, or untick the restriction.
          </span>
        )}
      </div>
    </div>
  );
}

function RegisterSystemCaller() {
  const api = useApi();
  const qc = useQueryClient();
  const [displayName, setDisplayName] = useState('');
  const [apimSubscriptionName, setApimSubscriptionName] = useState('');
  const [budget, setBudget] = useState('');
  const [notes, setNotes] = useState('');

  const create = useMutation({
    mutationFn: () =>
      api.adminCreateSystemCaller({
        displayName: displayName.trim(),
        apimSubscriptionName: apimSubscriptionName.trim(),
        monthlyBudgetUsd: budget.trim() === '' ? null : Number(budget),
        notes: notes.trim() || undefined
      }),
    onSuccess: () => {
      setDisplayName('');
      setApimSubscriptionName('');
      setBudget('');
      setNotes('');
      qc.invalidateQueries({ queryKey: ['admin-callers'] });
    }
  });

  const budgetInvalid = budget.trim() !== '' && (Number.isNaN(Number(budget)) || Number(budget) <= 0);
  const canSubmit = displayName.trim() !== '' && apimSubscriptionName.trim() !== '' && !budgetInvalid && !create.isPending;

  return (
    <div className="card" style={{ maxWidth: 640 }}>
      <div className="card-header"><h3>Register system caller</h3></div>
      <div className="card-body">
        <p style={{ color: 'var(--kt-grey-500)', marginBottom: 14, fontSize: '.9rem' }}>
          System callers authenticate with an APIM subscription key instead of a user token —
          use these for services and scheduled jobs. Create the subscription in APIM first; its
          display name is what identifies the caller here.
        </p>
        {create.error && <div className="error-banner">Could not register: {(create.error as Error).message}</div>}
        <div className="field-row">
          <div className="field">
            <label htmlFor="sys-name">Display name</label>
            <input id="sys-name" type="text" placeholder="e.g. Invoice summariser" value={displayName} onChange={e => setDisplayName(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="sys-apim">APIM subscription <strong>display name</strong></label>
            <input id="sys-apim" type="text" placeholder="e.g. invoice-summariser-prod" value={apimSubscriptionName} onChange={e => setApimSubscriptionName(e.target.value)} />
            <div className="hint">
              The subscription's <strong>display name</strong> in APIM — the name shown in the portal, not
              the resource ID or the key. Copy and paste it: APIM stamps that display name on every
              request and the gateway matches this field against it, so a mismatch (a stray space,
              a different spelling) means the caller is rejected as unregistered.
            </div>
          </div>
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="sys-budget">Monthly budget (USD)</label>
            <input id="sys-budget" type="number" min={0} step={1} placeholder="Unlimited" value={budget} onChange={e => setBudget(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="sys-notes">Notes</label>
            <input id="sys-notes" type="text" placeholder="Optional" value={notes} onChange={e => setNotes(e.target.value)} />
          </div>
        </div>
        <button className="btn-primary" onClick={() => create.mutate()} disabled={!canSubmit}>
          {create.isPending ? 'Registering...' : 'Register system caller'}
        </button>
      </div>
    </div>
  );
}
